import { extractApiErrorMessage } from '@/core/api/apiErrorMessage'
import { ocrScanRequestQueryOptions } from '@/core/api/endpoints/ocr'
import { useApiRef } from '@/core/api/useApi'
import {
  useStampQuizFetchFnFactory,
  useTanstackSpotFetchFn,
  useTanstackSpotWithLatLonFetchFnFactory,
} from '@/core/api/useTanstackFetchFn'
import { is401Error } from '@/core/api/xstateUtils'
import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import type { GpsFailureReason } from '@/core/geolocation/gpsFailureMessage'
import { resolveStampsUnavailableReason } from '@/domain/stamp/stampAvailability'
import { parseSpotIdFromHash } from '@/lib/qrUtils'
import type {
  AcquiredRewardData,
  OcrScanRequestGetResponse,
  QuizAnswerData,
  SpotDetailData,
  SpotGetResponse,
  StampAcquireResponse,
  StampQuizDetail,
  UserStampAcquireData,
} from '@/schemas/v2'
import type { ShareSnsData } from '@/schemas/v2/share-sns'
import type { SpotPhotoFrameResponse } from '@/schemas/v2/spot'
import type { StampUnavailableReason } from '@/schemas/v2/user'
import { useTokenStore } from '@/store/useTokenStore'
import { useQueryClient } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useMemo, useRef } from 'react'
import { assign, fromPromise, setup } from 'xstate'
import type { ErrorActorEvent } from 'xstate'
import { resolveTargetStamp } from './stampUtils'

// ---------------------------------------------------------------------------
// GPS ユーティリティ
// ---------------------------------------------------------------------------

/**
 * 採用する測位精度の上限（m）。これ以内の測位が届いた時点で確定する。
 * v1（`frontend/src` の `GEOLOCATION_ACCURACY`）と同値。
 */
export const GPS_ACCURACY_THRESHOLD = 80

/**
 * ページ入場時の精度ゲート打ち切り時間（ms）。
 *
 * `PositionOptions.timeout` は「測位が1件も取れない」場合にしか効かず、
 * 精度が足りない測位が届き続ける限りいつまでも確定しない（v1 はこれで無限に待っていた）。
 * そのため精度ゲート自体にも打ち切りを設け、期限が来たらそれまでの最良測位を採用する。
 *
 * この待ち時間はページ全体のスケルトン表示時間になる（initialLoading.prefetching は parallel で、
 * gps ブランチが final にならない限り画面が出ない）ため延ばさない（#3541）。
 */
export const GPS_ACCURACY_GATE_TIMEOUT = 10000

/**
 * 獲得ボタン押下時（gpsRefreshing）の精度ゲート打ち切り時間（ms）。
 *
 * 10 秒では iOS のコールドフィックスに間に合わず、失敗率が Android の 3 倍だった（#3541）。
 * この区間は「位置情報を取得中...」のモーダルを表示済みで、ユーザーも待つ意図で押しているため、
 * ページ入場時より長く待てる。
 */
export const GPS_ACQUIRE_GATE_TIMEOUT = 15000

/**
 * `timeout` は「測位を1件取得するまで」の上限で、精度ゲート（精度を満たすまでの上限）とは別の意味を持つ。
 *
 * timeout は精度ゲートより長くし、打ち切りの判断を必ず精度ゲート側に寄せる。
 * こうすることで「1件も届かない」ケースでも、打ち切りまでに届いた最良測位を捨てずに済む。
 *
 * maximumAge は 0 から 10 秒へ緩和した（#3541）。0 は毎回コールドフィックスを強制するため、
 * ページを開いた直後の iOS で間に合わず獲得が失敗していた。
 *
 * この画面で maximumAge が効くのは「入場時の測位」と「fromQr の自動起動」（detail.tsx が
 * idle 到達後に START_STAMP_FLOW を送る）で、通常はいずれも入場から数秒しか経っていない。
 * fromQr の自動起動は会員登録時アンケートの回答後まで待つため（#3649）入場から数分経つこともあるが、
 * その場合は maximumAge を超えて再測位されるだけで、入場時の古い座標が使われることはない。
 * よって 10 秒で足り、それ以上経過したケースは精度ゲートの延長（GPS_ACQUIRE_GATE_TIMEOUT）で拾う。
 *
 * AR フォト・音声ガイドの押下時の測位（tapLocationSource.ts、#3727）も getGPS() を通るため同じ値が効く。
 * こちらは「スタンプ獲得の直後に押した」ケースで直前の測位を再利用できる前提になっているので、
 * 0 に戻すと押下のたびにコールドフィックスを待つことになる。
 *
 * 30 秒まで許容しない理由は距離判定の誤差。徒歩 30 秒は約 40m でスタンプ範囲（35〜50m）と
 * 同オーダーになり判定を覆しうるが、10 秒なら約 13m に収まる。
 * とくに「スポットに近づいて再試行」の導線では、移動前のキャッシュ位置を掴むと再試行が無意味になる。
 *
 * 同種の判断の先例として、ボトムナビの「近くのスポット」（useApplicationTriggerFlowStateMachine）が
 * #3472 で `enableHighAccuracy: false` / `maximumAge: 60000` に緩和されている。
 * あちらは並び替え専用で距離判定に使わないため大きく取れる。こちらは獲得可否を左右するので短くする。
 */
const GEOLOCATION_OPTIONS: PositionOptions = { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 }

export type GPSResult = {
  lat: number
  lon: number
  accuracy: number
  available: boolean
  permissionDenied: boolean
  /** 取得失敗の理由。available: true のときは null（#3541） */
  failureReason: GpsFailureReason | null
}

/**
 * 「位置情報を取得できなかった」結果を生成する。
 * 呼び出しごとに新しいオブジェクトを返し、解決済みの結果が共有参照経由で書き換わらないようにする。
 *
 * permissionDenied は failureReason から導出する。既存の参照箇所
 * （detail.tsx の stampGpsPermissionDenied）との後方互換のために残す。
 *
 * 既定を null（理由なし）にしているのは、`isGpsRequired()` が false のときに
 * 「測位を試みていない」意味でこの関数が呼ばれるため。ここを 'TIMEOUT' 等にすると
 * 測位していないのに失敗理由が入り、ダイアログの文言出し分けを誤らせる。
 */
const gpsUnavailable = (failureReason: GpsFailureReason | null = null): GPSResult => ({
  lat: 0,
  lon: 0,
  accuracy: Number.POSITIVE_INFINITY,
  available: false,
  permissionDenied: failureReason === 'PERMISSION_DENIED',
  failureReason,
})

/**
 * 現在地を取得する。
 *
 * `watchPosition` で測位を受け取り続け、`GPS_ACCURACY_THRESHOLD` 以内の測位が届いた時点で確定する。
 * 期限（`GPS_ACCURACY_GATE_TIMEOUT`）までに基準を満たさなかった場合は、
 * それまでに届いた中で最も精度の良い測位を採用する（1件も届かなければ `available: false`）。
 *
 * 単発の `getCurrentPosition` では最初に届いた粗い測位（Wi-Fi・基地局測位で数百m〜数km）を
 * そのまま使ってしまい、スタンプの距離判定が誤爆するため `watchPosition` を使う。
 */
export const getGPS = (gateTimeoutMs: number = GPS_ACCURACY_GATE_TIMEOUT): Promise<GPSResult> =>
  new Promise<GPSResult>((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(gpsUnavailable('UNSUPPORTED'))
      return
    }

    let settled = false
    let watchId: number | null = null
    let timerId: ReturnType<typeof setTimeout> | null = null
    /** 精度ゲートを満たさなかった測位のうち最良のもの。打ち切り時のフォールバックに使う */
    let best: GPSResult | null = null
    /** 直近に届いたエラーの理由。1件も測位できずに打ち切った際の案内文言に使う（#3541） */
    let lastFailureReason: GpsFailureReason | null = null

    const settle = (result: GPSResult) => {
      if (settled) return
      settled = true
      if (timerId !== null) clearTimeout(timerId)
      if (watchId !== null) navigator.geolocation.clearWatch(watchId)
      resolve(result)
    }

    // 測位が1件も届かなければ、直近のエラー理由（無ければ TIMEOUT）で失敗を確定する
    timerId = setTimeout(() => settle(best ?? gpsUnavailable(lastFailureReason ?? 'TIMEOUT')), gateTimeoutMs)

    const id = navigator.geolocation.watchPosition(
      ({ coords }) => {
        const result: GPSResult = {
          lat: coords.latitude,
          lon: coords.longitude,
          accuracy: coords.accuracy,
          available: true,
          permissionDenied: false,
          failureReason: null,
        }
        if (coords.accuracy <= GPS_ACCURACY_THRESHOLD) {
          settle(result)
          return
        }
        if (best === null || coords.accuracy < best.accuracy) {
          best = result
        }
      },
      (error) => {
        // 権限拒否は待っても状況が変わらないため即座に打ち切る。
        // TIMEOUT / POSITION_UNAVAILABLE は W3C 仕様上 watch が継続するため、
        // 打ち切り時間まで待って最良測位が届いていればそれを採用する。
        if (error.code === error.PERMISSION_DENIED) {
          settle(gpsUnavailable('PERMISSION_DENIED'))
          return
        }
        // 打ち切り時に「なぜ取れなかったか」を案内できるよう理由だけ控える（#3541）
        lastFailureReason = error.code === error.TIMEOUT ? 'TIMEOUT' : 'POSITION_UNAVAILABLE'
      },
      GEOLOCATION_OPTIONS,
    )

    // コールバックが同期的に発火して settle 済みになっている場合に watch が残らないようにする
    if (settled) {
      navigator.geolocation.clearWatch(id)
    } else {
      watchId = id
    }
  })

// ---------------------------------------------------------------------------
// Context / Event 型
// ---------------------------------------------------------------------------

type GPSState = {
  lat: number
  lon: number
  accuracy: number
  available: boolean
  loaded: boolean
  permissionDenied: boolean
  /** 取得失敗の理由。available: true / 未測位のときは null（#3541） */
  failureReason: GpsFailureReason | null
  /**
   * 対象スタンプの獲得に位置情報が必要か。
   * 精度不足で獲得をブロックしてよいかの判定に使う（GPS 不要なスタンプまで止めないため）。
   */
  requiredForStamp: boolean
}

/** fetchUserStamps アクターの戻り値（獲得済み判定と獲得可否判定をまとめて返す） */
export type UserStampCheckResult = {
  /** 1件でも獲得済みか */
  hasAnyStamp: boolean
  /** スポットに紐づくスタンプを全件獲得済みか */
  hasAllStamps: boolean
  /** 獲得済みスタンプの ID */
  acquiredStampIds: number[]
  /** スタンプ ID → 獲得不可理由（獲得可能なら null）。獲得後の再計算に使う */
  unavailableReasonByStampId: Record<number, StampUnavailableReason | null>
  unavailableReason: StampUnavailableReason | null
}

export type StampFlowContext = {
  spotId: number
  isAuthenticated: boolean
  isAuthInitialized: boolean
  spot: SpotDetailData | null
  /**
   * 今回獲得を試みる対象スタンプの ID（日跨ぎによる変化を防ぐため確定後は固定）。
   * 複数スタンプが紐づく場合は「期間内かつ未獲得の先頭」（resolveTargetStamp）。
   */
  activeStampId: number | null
  /** 対象スタンプの種別（日跨ぎによる変化を防ぐため確定後は固定） */
  activeStampType: SpotDetailData['stamps'][number]['type'] | null
  /** 1件でも獲得済みか（ARフォトのアンロック・オーバーレイの獲得済み表示・再表示導線に使用） */
  hasAnyStamp: boolean
  /** 全件獲得済みか（ボタンラベル「取得済み」・非活性判定に使用） */
  hasAllStamps: boolean
  /** 獲得済みスタンプの ID（初回フェッチ + 獲得レスポンスで差分更新） */
  acquiredStampIds: number[]
  /**
   * スタンプ ID → サーバー判定の獲得不可理由（獲得可能なら null）。
   * 獲得直後にユーザースタンプを再フェッチせずボタン活性を再計算するために保持する。
   * 未認証・取得失敗時は空のままで、その場合は「理由不明＝押させる」に倒す。
   */
  unavailableReasonByStampId: Record<number, StampUnavailableReason | null>
  /**
   * サーバーが返した獲得不可の理由（`UserStampData.unavailableReason`）。
   * null は「獲得可能」または「未認証で未判定」。曜日・祝日・1日1回制限など
   * クライアントで判定できない条件を含むため、ボタンの非活性判定はこちらを優先する。
   * 複数スタンプの場合は「1件でも獲得可能なら null」（resolveStampsUnavailableReason）。
   */
  stampUnavailableReason: StampUnavailableReason | null
  /** スポットに紐づく全スタンプの ID（期間外も含む）。獲得済み判定の対象 */
  stampIds: number[]
  /** クイズが設定されているスタンプの ID（`SpotStampData.hasQuiz`）。クイズ prefetch の対象 */
  quizStampIds: number[]
  gps: GPSState
  /** 対象スタンプのクイズ（quizzesByStampId から activeStampId 分を取り出したもの） */
  quizzes: StampQuizDetail[]
  /** スタンプごとのクイズ。対象スタンプが後から確定するため全件分を保持する */
  quizzesByStampId: Record<number, StampQuizDetail[]>
  /** 再表示フローで送り送りする対象（獲得済みのみ・スタンプ ID 昇順） */
  redisplayStampIds: number[]
  acquiredStamps: UserStampAcquireData[]
  currentStampIndex: number
  acquiredRewards: AcquiredRewardData[]
  /** 拡散用SNS（#3285）。スタンプ獲得レスポンスの shareSns */
  shareSns: ShareSnsData | null
  /** 獲得APIが返したエラーコード。表示文言の解決に使う */
  acquireErrorCode: string | null
  /** 獲得APIが返した利用者向け文言。i18n キーが無いコードの表示に使う（#3541） */
  acquireErrorMessage: string | null
  pendingChoiceId: number | null
  pendingTextAnswer: string | null
  quizAnswerResult: QuizAnswerData | null
  quizDailyLimitExhausted: boolean
  qrCode: string | null
  ocrCapturedImage: string | null
  ocrFailureId: number | null
  /** OCRスタンプ失敗エラーが返した maxRetry（画像送信リトライ回数の上限値。残数ではない）。null は再撮影不可 */
  ocrMaxRetry: number | null
  /** 送信して失敗し再撮影した回数（OCR_FAILURE_RETRY のみ加算。プレビュー撮り直しは含めない） */
  ocrRetryCount: number
  /** スタンプ獲得によって ARカメラが利用可能になったことを通知するフラグ */
  showArPhotoUnlocked: boolean
  /** ページ入場時の POST /photo-frames 結果がスタンプ必要（stamp_required）だったか */
  photoFrameInitiallyStampRequired: boolean
}

type StampFlowEvent =
  | { type: 'AUTH_READY'; isAuthenticated: boolean }
  | { type: 'START_STAMP_FLOW' }
  | { type: 'NOT_AUTH_ERROR_CLOSE' }
  | { type: 'POPUP_CLOSE' }
  | { type: 'QUIZ_CLOSE' }
  | { type: 'ANSWER_SUBMIT'; choiceId: number }
  | { type: 'TEXT_ANSWER_SUBMIT'; answer: string }
  | { type: 'QUIZ_RETRY' }
  | { type: 'STAMP_GET' }
  | { type: 'STAMP_NEXT' }
  | { type: 'QR_READ'; code: string }
  | { type: 'QR_CLOSE' }
  | { type: 'BARCODE_READ'; code: string }
  | { type: 'BARCODE_CLOSE' }
  | { type: 'REWARD_CLOSE' }
  | { type: 'PHOTO_CLOSE' }
  | { type: 'STAMP_REDISPLAY' }
  | { type: 'STAMP_REDISPLAY_POPUP_CLOSE' }
  | { type: 'STAMP_REDISPLAY_STAMP_NEXT' }
  | { type: 'ACQUIRE_ERROR_CLOSE' }
  | { type: 'GPS_RANGE_ERROR_CLOSE' }
  | { type: 'GPS_RANGE_ERROR_RETRY' }
  | { type: 'GPS_ACCURACY_ERROR_CLOSE' }
  | { type: 'GPS_ACCURACY_ERROR_RETRY' }
  | { type: 'GPS_UNAVAILABLE_ERROR_CLOSE' }
  | { type: 'GPS_UNAVAILABLE_ERROR_RETRY' }
  | { type: 'RESET' }
  /** skipNext: 「次回から表示しない」のチェック状態（#3695） */
  | { type: 'OCR_WARNING_CONFIRM'; skipNext: boolean }
  | { type: 'OCR_WARNING_CLOSE' }
  | { type: 'OCR_CAPTURED'; image: string }
  | { type: 'OCR_CAPTURE_CLOSE' }
  | { type: 'OCR_PREVIEW_SUBMIT' }
  | { type: 'OCR_PREVIEW_RETAKE' }
  | { type: 'OCR_PREVIEW_CLOSE' }
  | { type: 'OCR_FAILURE_RETRY' }
  | { type: 'OCR_FAILURE_APPLY' }
  | { type: 'OCR_FAILURE_CLOSE' }
  | { type: 'QR_WRONG_FORMAT' }
  | { type: 'QR_WRONG_FORMAT_CLOSE' }
  | { type: 'QUIZ_DAILY_LIMIT_CLOSE' }
  /** トークンリフレッシュ完了後に spotFetching からやり直す（401 エラー時） */
  | { type: 'REFRESH_COMPLETE' }
  | { type: 'AR_PHOTO_UNLOCKED_CLOSE' }

// Actor input 型
type QuizzesInput = { stampIds: number[] }
type QuizAnswerInput = { stampId: number; quizId: number; choiceId: number | null; textAnswer: string | null }
export type AcquireInput = {
  stampId: number
  lat: number
  lon: number
  spotId: number
  qrCode: string | null
  ocrImage: string | null
  gpsAvailable: boolean
  stampType: SpotDetailData['stamps'][number]['type'] | undefined
}
type SpotWithGpsInput = { spotId: number; lat: number; lon: number }
type FetchPhotoFramesInput = { spotId: number; isAuthenticated: boolean }

/** キャンペーン側の GPS 併用設定（`CampaignData` の同名フラグ） */
export type AcquireStampGpsOptions = {
  isQRCodeWithGPS: boolean
  isBarCodeWithGPS: boolean
  isKeywordWithGPS: boolean
}

/** スポットに紐づくスタンプの種別 */
export type SpotStampType = SpotDetailData['stamps'][number]['type']

/** fetchGPS アクターの入力。この画面で位置情報が要るかどうかの判定材料 */
export type FetchGpsInput = {
  /**
   * スポットに紐づく**全**スタンプの種別。
   *
   * 対象スタンプ（activeStampType）は prefetching の時点では獲得状態が未確定の暫定値で、
   * 「先頭が獲得済みのキーワード、実際の対象は GPS」のようなケースで必要な測位を取り逃す。
   * 測位するかどうかは取り逃さない側に倒すため、期間外も含む全件で判定する。
   */
  stampTypes: SpotStampType[]
  /** 今回獲得を試みる対象スタンプの種別。精度不足で止めてよいかの判定に使う */
  activeStampType: SpotStampType | null
  /**
   * 精度ゲートの打ち切り時間（ms）。ページ入場時と獲得押下時で待たせてよい長さが違う（#3541）。
   * 省略時は getGPS() の既定（GPS_ACCURACY_GATE_TIMEOUT）。
   * `isGpsRequired()` はこの値を使わないため省略可にしている。
   */
  gateTimeoutMs?: number
}

/** fetchGPS アクターの戻り値。測位結果に「対象スタンプに必要か」を添えて返す */
export type FetchGpsResult = GPSResult & {
  requiredForStamp: boolean
}

/** `isGpsRequired` の判定に使うキャンペーン設定 */
export type GpsRequirementOptions = AcquireStampGpsOptions & {
  /**
   * キャンペーン情報を取得済みか。
   * 未取得だと GPS 併用フラグが既定値 false のまま判定され、本来必要な測位を
   * 取り逃してスタンプを獲得できなくなる。未取得のうちは「必要」に倒す。
   */
  isCampaignLoaded: boolean
}

/**
 * 対象スタンプの獲得に位置情報が要るかを判定する。
 *
 * GPS 種別に加え、キャンペーンで GPS 併用が有効な QR / バーコード / キーワードも対象。
 * サーバーはこれらで座標が無いと STAMP_LOCATION_REQUIRED を返すため、
 * 「測位するか」と「位置情報が無いときにボタンを止めるか」の両方の基準になる。
 */
export const isGpsRequiredForStamp = (stampType: SpotStampType | null, options: AcquireStampGpsOptions): boolean => {
  switch (stampType) {
    case 'GPS':
      return true
    case 'QR_CODE':
      return options.isQRCodeWithGPS
    case 'BARCODE':
      return options.isBarCodeWithGPS
    case 'KEYWORD':
      return options.isKeywordWithGPS
    // OCR の GPS 併用はスタンプ単位の設定でクライアントからは判別できないため、
    // 座標も送っていない（buildAcquireStampBody 参照）。よってここでは不要とする。
    case 'OCR':
      return false
    // 対象スタンプが未確定（期間外・全件獲得済みなど）
    default:
      return false
  }
}

/**
 * この画面で位置情報の取得が必要かを判定する。
 *
 * v1 は「GPS を使うスタンプのときだけ初期測位を行う」仕様だった（v1 detail.tsx:662-674）。
 * 測位（特に精度が出るまでの待機）はキャンペーンによっては純粋な待ち時間になるため、同じ条件に揃える。
 *
 * AR フォト・音声ガイドの距離判定用の座標は押下時に測位する（detail.tsx の handleArPhotoClick /
 * handleAudioGuideClick、#3727）ため、ここでは見ない。以前はこれらがあるスポットでも入場時に測位しており、
 * GPS 不要のスタンプしか無いスポットでもスケルトンが最大 10 秒延び、許可ダイアログが出ていた。
 */
export const isGpsRequired = (input: FetchGpsInput, options: GpsRequirementOptions): boolean => {
  // キャンペーン情報が未取得のうちは判定できないため、取得する側に倒す
  if (!options.isCampaignLoaded) return true

  // 1件でも位置情報を要するスタンプがあれば測位する
  return input.stampTypes.some((stampType) => isGpsRequiredForStamp(stampType, options))
}

/**
 * 獲得ボタン押下時に測位し直すかを判定する（#3725）。
 *
 * isGpsRequired() がスポットの全スタンプで判定するのに対し、こちらは対象スタンプだけを見る。
 * 押下時の測位はスタンプ獲得のためのもので、GPS 不要の QR で「位置情報を取得中...」を最大 15 秒出さないため。
 *
 * キャンペーン情報が未取得のうちは GPS 併用フラグを判定できないため、測位する側に倒す（isGpsRequired と同じ方針）。
 */
export const shouldRefreshGpsOnStart = (stampType: SpotStampType | null, options: GpsRequirementOptions): boolean =>
  !options.isCampaignLoaded || isGpsRequiredForStamp(stampType, options)

/**
 * スタンプ獲得 API のリクエストボディを組み立てる。
 *
 * 位置情報を取得できていない（`gpsAvailable: false`）場合は lat/lon を一切含めない（#3445）。
 * `getGPS()` は失敗時に (0,0) を返すため、そのまま送るとサーバーは「有効な座標」として
 * 距離を計算し、実際の原因（GPS 未取得）ではなく「範囲外」（STAMP_RANGE_OUT / STAMP_GPS_OUT_OF_RANGE）と
 * 判定してしまう。座標を省けばサーバーは STAMP_LOCATION_REQUIRED を返すため、原因が切り分けられる。
 *
 * OCR は元から座標を送らない（サーバー側 `ocrConfig.withGps` の判定には未対応）。
 */
export const buildAcquireStampBody = (
  input: AcquireInput,
  options: AcquireStampGpsOptions,
): Record<string, unknown> => {
  const body: Record<string, unknown> = { spotId: input.spotId }
  const coordinates = input.gpsAvailable ? { lat: input.lat, lon: input.lon } : {}

  switch (input.stampType) {
    case 'QR_CODE':
      body.code = input.qrCode
      if (options.isQRCodeWithGPS) Object.assign(body, coordinates)
      break
    case 'BARCODE':
      body.code = input.qrCode
      if (options.isBarCodeWithGPS) Object.assign(body, coordinates)
      break
    case 'GPS':
      Object.assign(body, coordinates)
      break
    case 'KEYWORD':
      body.keyword = true
      if (options.isKeywordWithGPS) Object.assign(body, coordinates)
      break
    case 'OCR':
      if (input.ocrImage !== null) {
        // DataURL 形式の場合は純粋 Base64 部分のみ抽出
        body.image = input.ocrImage.includes(',') ? input.ocrImage.split(',')[1] : input.ocrImage
      }
      break
    default:
      break
  }

  return body
}

// ---------------------------------------------------------------------------
// ベースマシン
// ---------------------------------------------------------------------------

export const stampFlowMachineBase = setup({
  types: {
    context: {} as StampFlowContext,
    events: {} as StampFlowEvent,
    input: {} as {
      spotId: number
      isAuthenticated: boolean
      isAuthInitialized: boolean
    },
  },
  actors: {
    fetchSpot: fromPromise<SpotGetResponse | undefined, void>(async () => undefined),
    fetchGPS: fromPromise<FetchGpsResult, FetchGpsInput>(async () => ({
      lat: 0,
      lon: 0,
      accuracy: Number.POSITIVE_INFINITY,
      available: false,
      permissionDenied: false,
      failureReason: null,
      requiredForStamp: false,
    })),
    fetchQuizzes: fromPromise<Record<number, StampQuizDetail[]>, QuizzesInput>(async () => ({})),
    answerQuiz: fromPromise<{ quiz: QuizAnswerData }, QuizAnswerInput>(async () => ({
      quiz: {
        quizId: 0,
        isCorrect: false,
        answer: null,
        message: null,
        hintMessage: null,
        correctImage: null,
        incorrectImage: null,
        retry: true,
      },
    })),
    acquireStamp: fromPromise<StampAcquireResponse, AcquireInput>(async () => ({ stamps: [], acquiredRewards: [] })),
    fetchOcrScanRequest: fromPromise<OcrScanRequestGetResponse, { ocrId: number }>(async () => ({
      ocrId: 0,
      allowApplication: false,
      applicable: false,
      messageMaxLength: 0,
      fieldValueMaxLength: 0,
      fields: [],
    })),
    fetchSpotWithGps: fromPromise<SpotGetResponse | undefined, SpotWithGpsInput>(async () => undefined),
    fetchUserStamps: fromPromise<UserStampCheckResult, { stampIds: number[] }>(async () => ({
      hasAnyStamp: false,
      hasAllStamps: false,
      acquiredStampIds: [],
      unavailableReasonByStampId: {},
      unavailableReason: null,
    })),
    fetchPhotoFrames: fromPromise<SpotPhotoFrameResponse, FetchPhotoFramesInput>(async () => ({ photoFrames: [] })),
  },
  guards: {
    isNotAuthenticated: ({ context }) => !context.isAuthenticated,
    is401Error: ({ event }) => is401Error((event as unknown as ErrorActorEvent).error),
    hasPopup: () => false,
    hasQuiz: ({ context }) => context.quizzes.length > 0,
    isDescriptiveQuiz: ({ context }) => context.quizzes[0]?.quizType === 1,
    isQrType: ({ context }) => context.activeStampType === 'QR_CODE',
    /**
     * 読み込んだ QR が別スポットのものか（#3725）。
     *
     * ハッシュは `{stampCode}-{spotId}` 形式で、サーバーは spotId が一致しないと
     * STAMP_CODE_MISMATCH を返す（validateQrOrBarcode）。獲得 API まで進めると
     * クイズに回答させた後でエラーになるため、QR_READ の時点で同じ判定を先に行う。
     *
     * spotId をパースできないハッシュも獲得 API では必ず STAMP_CODE_MISMATCH になるため、ここで弾く。
     * stampCode 部分はフロントでは検証できない（SpotStampData に code を含めない）ため、従来どおりサーバーに委ねる。
     */
    isQrSpotMismatch: ({ context, event }) =>
      event.type === 'QR_READ' && parseSpotIdFromHash(event.code) !== context.spotId,
    /**
     * 獲得ボタン押下時に測位し直す必要があるか（#3725）。
     * キャンペーン設定を参照するため実装は provide() で差し込む（shouldRefreshGpsOnStart）。
     * ベースは従来挙動（常に測位）に倒す。
     */
    isGpsRequiredForActiveStamp: () => true,
    isBarcodeType: ({ context }) => context.activeStampType === 'BARCODE',
    isOcrType: ({ context }) => context.activeStampType === 'OCR',
    hasOcrWarning: ({ context }) =>
      context.spot?.stamps.find((s) => s.id === context.activeStampId)?.ocrWarning?.enabled === true,
    /** 「次回から表示しない」保存済みか（#3695）。localStorage の参照は provide() で差し込む */
    isOcrWarningSkipped: () => false,
    /** OCR_WARNING_CONFIRM で「次回から表示しない」がチェックされているか */
    isOcrWarningSkipNextChecked: ({ event }) => event.type === 'OCR_WARNING_CONFIRM' && event.skipNext,
    hasPhotoUpload: () => false,
    hasMoreStamps: ({ context }) => context.currentStampIndex < context.acquiredStamps.length - 1,
    /** 再表示フロー用。獲得直後フローの hasMoreStamps とは対象リストが異なる */
    hasMoreRedisplayStamps: ({ context }) => context.currentStampIndex < context.redisplayStampIds.length - 1,
    noStampId: ({ context }) => context.activeStampId === null,
    noStampIds: ({ context }) => context.stampIds.length === 0,
    noQuizStampIds: ({ context }) => context.quizStampIds.length === 0,
    hasRewards: ({ context }) => context.acquiredRewards.length > 0,
    /**
     * サーバー判定で獲得可能範囲の外にいる状態。
     *
     * 位置情報が不要なスタンプ（GPS 併用なしの QR・キーワード等）まで止めないよう requiredForStamp を見る（#3541）。
     * isInRange はキャンペーン未ロード時に「必要側に倒して」測位しただけのスポットや、
     * 複数スタンプのうち別のスタンプのために測位したスポットでも算出されるため、
     * これが無いと「距離を要求しないスタンプが距離で弾かれる」ことになる。
     * 同じ popupCheck の isGpsUnavailableForStamp / isGpsAccuracyInsufficient と条件を揃えている。
     */
    outOfRange: ({ context }) => context.gps.requiredForStamp && context.spot?.isInRange === false,
    /**
     * 位置情報が必要なスタンプなのに測位できていない状態（#3541）。
     *
     * この状態で獲得 API を呼ぶと、座標を送らない（#3445）ため必ず STAMP_LOCATION_REQUIRED になる。
     * ユーザーには原因が分からず平均 4.4 回のリトライを生んでいたため、API を呼ばずに理由を案内する。
     *
     * isGpsAccuracyInsufficient が gps.available === true 前提なのに対し、こちらは false 側を受け持つ。
     * 位置情報が不要なスタンプ（OCR・GPS 併用なしの QR 等）まで止めないよう requiredForStamp を見る。
     */
    isGpsUnavailableForStamp: ({ context }) =>
      context.gps.requiredForStamp && context.gps.loaded && !context.gps.available,
    /**
     * 測位はできたが精度が閾値を満たさない状態。
     * この座標で距離判定を行うと誤判定になるため獲得 API は呼ばず、測位のやり直しを促す。
     * 位置情報が不要なスタンプ（OCR・GPS 併用なしの QR 等）まで止めないよう requiredForStamp を見る。
     */
    isGpsAccuracyInsufficient: ({ context }) =>
      context.gps.requiredForStamp && context.gps.available && context.gps.accuracy > GPS_ACCURACY_THRESHOLD,
    canRetryQuiz: ({ context }) => context.quizAnswerResult?.retry !== false,
    isQuizDailyLimitExhausted: ({ context }) => context.quizDailyLimitExhausted,
    // maxRetry が null（OCR設定参照不可等）または 0 の場合は再撮影不可。数値かつ1以上で残数があるときのみ再撮影可
    canRetryOcr: ({ context }) =>
      context.ocrMaxRetry != null && context.ocrMaxRetry > 0 && context.ocrRetryCount < context.ocrMaxRetry,
    hasOcrFailureId: ({ context }) => context.ocrFailureId !== null,
  },
  actions: {
    /** 「次回から表示しない」を保存する（#3695）。localStorage への書き込みは provide() で差し込む */
    saveOcrWarningSkip: () => {},
    clearScanContext: assign({ qrCode: () => null, ocrCapturedImage: () => null }),
    clearArPhotoUnlocked: assign({ showArPhotoUnlocked: () => false }),
    /**
     * 測位をスキップしたときに、今回の対象スタンプが位置情報を要さないことを確定させる（#3725）。
     * 入場時の fetchGPS は暫定の activeStampType で requiredForStamp を算出しており、
     * resolveActiveStamp で対象が変わった場合に古い true が残ると popupCheck の GPS ガードで誤って止まるため。
     * 座標（lat/lon/available）は AR フォト・音声ガイドが使うので入場時の値を残す。
     */
    markGpsNotRequiredForStamp: assign({
      gps: ({ context }) => ({ ...context.gps, requiredForStamp: false }),
    }),
    /**
     * 再表示フローの対象リストを組み立てる。
     * 獲得済みのみを スタンプ ID 昇順（スポット一覧のピン画像・オーバーレイと同じ基準）で並べる。
     */
    buildRedisplayStamps: assign(({ context }) => ({
      redisplayStampIds: (context.spot?.stamps ?? [])
        .map((s) => s.id)
        .filter((id) => context.acquiredStampIds.includes(id))
        .sort((a, b) => a - b),
      currentStampIndex: 0,
    })),
    /**
     * 獲得状態を加味して対象スタンプ（activeStampId / activeStampType / quizzes）を再解決する。
     *
     * キャンペーン押印期間はスポット内の全スタンプに等しくかかるため「どのスタンプを選ぶか」には
     * 影響しない。よってここではスタンプ固有の期間・営業時間のみで判定し、キャンペーン期間による
     * 期間外判定は detail.tsx の isOutOfPlayingPeriod（campaignData を参照）に委ねる。
     *
     * サーバー判定で獲得不可のスタンプ（曜日・祝日・1日1回制限など）は対象から外す。
     * ボタンは「1件でも獲得可能なら活性」のため、外さないと活性のまま獲得不可スタンプを叩いてしまう。
     */
    resolveActiveStamp: assign(({ context }) => {
      const unavailableStampIds = new Set(
        Object.entries(context.unavailableReasonByStampId)
          .filter(([, reason]) => reason !== null)
          .map(([id]) => Number(id)),
      )
      const target = resolveTargetStamp(
        context.spot?.stamps ?? [],
        {},
        new Set(context.acquiredStampIds),
        new Date(),
        unavailableStampIds,
      )
      return {
        activeStampId: target?.id ?? null,
        activeStampType: target?.type ?? null,
        quizzes: target != null ? (context.quizzesByStampId[target.id] ?? []) : [],
      }
    }),
  },
}).createMachine({
  id: 'stampFlow',
  context: ({ input }) => ({
    spotId: input.spotId,
    isAuthenticated: input.isAuthenticated,
    isAuthInitialized: input.isAuthInitialized,
    spot: null,
    activeStampId: null,
    activeStampType: null,
    hasAnyStamp: false,
    hasAllStamps: false,
    acquiredStampIds: [],
    unavailableReasonByStampId: {},
    stampUnavailableReason: null,
    stampIds: [],
    quizStampIds: [],
    gps: {
      lat: 0,
      lon: 0,
      accuracy: Number.POSITIVE_INFINITY,
      available: false,
      loaded: false,
      permissionDenied: false,
      failureReason: null,
      requiredForStamp: false,
    },
    quizzes: [],
    quizzesByStampId: {},
    redisplayStampIds: [],
    acquiredStamps: [],
    currentStampIndex: 0,
    acquiredRewards: [],
    shareSns: null,
    acquireErrorCode: null,
    acquireErrorMessage: null,
    pendingChoiceId: null,
    pendingTextAnswer: null,
    quizAnswerResult: null,
    quizDailyLimitExhausted: false,
    qrCode: null,
    ocrCapturedImage: null,
    ocrFailureId: null,
    ocrMaxRetry: null,
    ocrRetryCount: 0,
    showArPhotoUnlocked: false,
    photoFrameInitiallyStampRequired: false,
  }),
  initial: 'initialLoading',
  // AR_PHOTO_UNLOCKED_CLOSE はどのステートからでも閉じられるようにルートレベルで処理
  on: {
    AR_PHOTO_UNLOCKED_CLOSE: { actions: 'clearArPhotoUnlocked' },
  },
  states: {
    // ---------------------------------------------------------------
    // 初期ロード
    // ---------------------------------------------------------------
    initialLoading: {
      initial: 'waitingForAuth',
      states: {
        // 認証状態が確定するまで待機する。
        // getState() で既に確定済みなら always で即スキップ。
        waitingForAuth: {
          always: [{ guard: ({ context }) => context.isAuthInitialized, target: 'spotFetching' }],
          on: {
            AUTH_READY: {
              target: 'spotFetching',
              actions: assign({
                isAuthenticated: ({ event }) => event.isAuthenticated,
                isAuthInitialized: () => true,
              }),
            },
          },
        },
        spotFetching: {
          invoke: {
            src: 'fetchSpot',
            onDone: {
              target: 'prefetching',
              actions: [
                assign({
                  spot: ({ event }) => event.output?.spot ?? null,
                  // 獲得済み判定は全スタンプを対象にする
                  stampIds: ({ event }) => (event.output?.spot?.stamps ?? []).map((s) => s.id),
                  // クイズ prefetch は hasQuiz のスタンプのみ（#3424 で追加。旧レスポンスでは既定 true）
                  quizStampIds: ({ event }) =>
                    (event.output?.spot?.stamps ?? []).filter((s) => s.hasQuiz).map((s) => s.id),
                }),
                // この時点では獲得状態が未取得のため、期間だけで暫定の対象を決める。
                // 獲得状態が確定した後に prefetching.onDone で再解決する。
                'resolveActiveStamp',
              ],
            },
            onError: [{ guard: 'is401Error', target: '#stampFlow.waitingForRefresh' }, { target: '#stampFlow.error' }],
          },
        },
        prefetching: {
          type: 'parallel' as const,
          states: {
            quiz: {
              initial: 'fetching',
              states: {
                fetching: {
                  always: [{ guard: 'noQuizStampIds', target: 'done' }],
                  invoke: {
                    // 対象スタンプは userStampCheck（獲得状態）の完了後に確定するため、
                    // クイズを持つスタンプ分を先に取得して map に保持し、確定後に取り出す。
                    src: 'fetchQuizzes',
                    input: ({ context }): QuizzesInput => ({ stampIds: context.quizStampIds }),
                    onDone: {
                      target: 'done',
                      actions: assign({
                        quizzesByStampId: ({ event }) => event.output,
                      }),
                    },
                    onError: { target: 'done' },
                  },
                },
                done: { type: 'final' as const },
              },
            },
            gps: {
              initial: 'fetching',
              states: {
                fetching: {
                  invoke: {
                    src: 'fetchGPS',
                    input: ({ context }): FetchGpsInput => ({
                      stampTypes: (context.spot?.stamps ?? []).map((s) => s.type),
                      activeStampType: context.activeStampType,
                      // ページ全体のスケルトン表示時間になるため延ばさない（#3541）
                      gateTimeoutMs: GPS_ACCURACY_GATE_TIMEOUT,
                    }),
                    onDone: {
                      target: 'done',
                      actions: assign({ gps: ({ event }) => ({ ...event.output, loaded: true }) }),
                    },
                    onError: {
                      target: 'done',
                      // gpsRefreshing の onError と同じ扱い。requiredForStamp を false で焼き付けると
                      // popupCheck のガードが黙って無効化されるため、スタンプ側の要件は維持する
                      actions: assign({
                        gps: ({ context }) => ({
                          ...context.gps,
                          lat: 0,
                          lon: 0,
                          accuracy: Number.POSITIVE_INFINITY,
                          available: false,
                          permissionDenied: false,
                          // アクター自体の失敗であり測位の失敗ではないため理由は持たせない
                          failureReason: null,
                          loaded: true,
                        }),
                      }),
                    },
                  },
                },
                done: { type: 'final' as const },
              },
            },
            userStampCheck: {
              initial: 'checking',
              states: {
                checking: {
                  always: [
                    { guard: 'isNotAuthenticated', target: 'done' },
                    { guard: 'noStampIds', target: 'done' },
                  ],
                  invoke: {
                    src: 'fetchUserStamps',
                    input: ({ context }) => ({ stampIds: context.stampIds }),
                    onDone: {
                      target: 'done',
                      actions: assign({
                        hasAnyStamp: ({ event }) => event.output.hasAnyStamp,
                        hasAllStamps: ({ event }) => event.output.hasAllStamps,
                        acquiredStampIds: ({ event }) => event.output.acquiredStampIds,
                        unavailableReasonByStampId: ({ event }) => event.output.unavailableReasonByStampId,
                        stampUnavailableReason: ({ event }) => event.output.unavailableReason,
                      }),
                    },
                    onError: { target: 'done' },
                  },
                },
                done: { type: 'final' as const },
              },
            },
            photoFrameCheck: {
              initial: 'checking',
              states: {
                checking: {
                  always: [{ guard: ({ context }) => !context.spot?.photoFrame, target: 'done' }],
                  invoke: {
                    src: 'fetchPhotoFrames',
                    input: ({ context }): FetchPhotoFramesInput => ({
                      spotId: context.spotId,
                      isAuthenticated: context.isAuthenticated,
                    }),
                    onDone: {
                      target: 'done',
                      actions: assign({
                        photoFrameInitiallyStampRequired: ({ event }) => {
                          const frames = event.output.photoFrames
                          return (
                            frames.length > 0 &&
                            frames.every((e) => e.status !== 'available') &&
                            frames.some((e) => e.status === 'stamp_required')
                          )
                        },
                      }),
                    },
                    onError: { target: 'done' },
                  },
                },
                done: { type: 'final' as const },
              },
            },
          },
          // 獲得状態が確定したので、未獲得を優先する形で対象スタンプを確定させる
          onDone: { target: 'spotGpsFetching', actions: 'resolveActiveStamp' },
        },
        spotGpsFetching: {
          always: [{ guard: ({ context }) => !context.gps.available, target: '#stampFlow.idle' }],
          invoke: {
            src: 'fetchSpotWithGps',
            input: ({ context }): SpotWithGpsInput => ({
              spotId: context.spotId,
              lat: context.gps.lat,
              lon: context.gps.lon,
            }),
            onDone: {
              target: '#stampFlow.idle',
              actions: assign({
                spot: ({ event }) => event.output?.spot ?? null,
              }),
            },
            onError: { target: '#stampFlow.idle' },
          },
        },
      },
    },
    // ---------------------------------------------------------------
    // Idle
    // ---------------------------------------------------------------
    idle: {
      // スポット詳細に戻ったら OCRリトライ関連の状態をリセット（撮影の一連の流れ単位でカウント）
      entry: assign({ ocrRetryCount: () => 0, ocrMaxRetry: () => null, ocrFailureId: () => null }),
      on: {
        START_STAMP_FLOW: [
          { guard: 'isNotAuthenticated', target: 'notAuthenticatedError', actions: 'clearArPhotoUnlocked' },
          // ページ滞在中に日付や獲得状態が変わっている可能性があるため対象スタンプを再解決する。
          // 再解決後の対象で測位要否を判定したいが、XState v5 のガードは遷移の actions より前に
          // 評価されるため、stampFlowStarting を挟んで判定する（#3725）
          { target: 'stampFlowStarting', actions: ['clearArPhotoUnlocked', 'resolveActiveStamp'] },
        ],
        STAMP_REDISPLAY: { target: 'redisplayPopupCheck', actions: ['clearArPhotoUnlocked', 'buildRedisplayStamps'] },
      },
    },
    /**
     * 獲得フローの入口（#3725）。対象スタンプが位置情報を要するときだけ測位する。
     * GPS 不要（例: GPS 併用オフの QR）なら gpsRefreshing / spotRangeRefreshing を経由せず popupCheck へ進み、
     * 「位置情報を取得中...」を表示しない。
     */
    stampFlowStarting: {
      always: [
        { guard: 'isGpsRequiredForActiveStamp', target: 'gpsRefreshing' },
        { target: 'popupCheck', actions: 'markGpsNotRequiredForStamp' },
      ],
    },
    // ---------------------------------------------------------------
    // GPS 再取得（スタンプ獲得ボタン押下時）
    // ---------------------------------------------------------------
    gpsRefreshing: {
      invoke: {
        src: 'fetchGPS',
        input: ({ context }): FetchGpsInput => ({
          stampTypes: (context.spot?.stamps ?? []).map((s) => s.type),
          activeStampType: context.activeStampType,
          // 「位置情報を取得中...」表示中なのでページ入場時より長く待てる（#3541）
          gateTimeoutMs: GPS_ACQUIRE_GATE_TIMEOUT,
        }),
        onDone: {
          target: 'spotRangeRefreshing',
          actions: assign({ gps: ({ event }) => ({ ...event.output, loaded: true }) }),
        },
        onError: {
          target: 'spotRangeRefreshing',
          // アクターが失敗した場合は測位結果の信頼性が担保できない。前回の成功座標を残すと
          // 「入場時の古い座標を現在地として獲得APIに送る」ことになり、本Issueで潰した問題に戻るため
          // 未取得状態へ倒す。requiredForStamp は測位結果ではなくスタンプ側の要件なので維持し、
          // 位置情報が必要なスタンプなら popupCheck のガードで確実に止める。
          actions: assign({
            gps: ({ context }) => ({
              ...context.gps,
              lat: 0,
              lon: 0,
              accuracy: Number.POSITIVE_INFINITY,
              available: false,
              permissionDenied: false,
              // 測位そのものの失敗ではないため理由は持たせない（案内は「原因不明のエラー」になる）
              failureReason: null,
              loaded: true,
            }),
          }),
        },
      },
    },
    // ---------------------------------------------------------------
    // 距離判定の取り直し（獲得ボタン押下・再試行時）
    // ---------------------------------------------------------------
    /**
     * 測位し直した座標で `spot.isInRange` を取り直す（#3541）。
     *
     * `isInRange` はサーバーが lat/lon 付きのスポット詳細から算出する値で、
     * 以前は initialLoading の spotGpsFetching でしか更新していなかった。
     * そのため「ページを開いた時点で範囲外だと、スポットに到着して再試行しても
     * outOfRange ガードが古い false を見続け、リロードするまで獲得できない」状態だった。
     * gpsRefreshing の直後に置くことで、ボタン押下・各種リトライのすべてが最新の判定を使う。
     */
    spotRangeRefreshing: {
      always: [
        // 距離判定を取り直す意味があるのは「位置情報が必要なスタンプ」かつ「信頼できる座標がある」ときだけ。
        // それ以外は API を呼ばず popupCheck の各ガードによる案内に任せる。
        //
        // requiredForStamp が false（キャンペーン未ロード時や別スタンプのために測位しただけ等）のときは
        // outOfRange ガードも SpotDetail の stampOutOfRange も成立しないため、
        // 取り直した isInRange の使い道が無い（PR #3542 レビュー指摘）。
        { guard: ({ context }) => !context.gps.requiredForStamp, target: 'popupCheck' },
        // 座標が無ければ距離判定が成立しない（requiredForStamp が true なら popupCheck 側で案内する）
        { guard: ({ context }) => !context.gps.available, target: 'popupCheck' },
        // 粗い座標で算出した isInRange は誤判定になるため、取り直さず精度不足として案内する
        { guard: 'isGpsAccuracyInsufficient', target: 'popupCheck' },
      ],
      invoke: {
        src: 'fetchSpotWithGps',
        input: ({ context }): SpotWithGpsInput => ({
          spotId: context.spotId,
          lat: context.gps.lat,
          lon: context.gps.lon,
        }),
        onDone: {
          target: 'popupCheck',
          // 取得できなかった場合に spot を null で上書きするとページ全体が描画不能になるため、
          // 直前の値を残す（initialLoading 側とは違い、ここは表示中の画面を壊さないことを優先する）
          actions: assign({ spot: ({ context, event }) => event.output?.spot ?? context.spot }),
        },
        // 取り直しに失敗したら従来どおり古い isInRange のまま進む（サーバー側の距離判定に委ねる）
        onError: { target: 'popupCheck' },
      },
    },
    // ---------------------------------------------------------------
    // スタンプ獲得フロー
    // ---------------------------------------------------------------
    popupCheck: {
      always: [
        { guard: 'noStampId', target: 'idle' },
        // 座標が無い場合は距離判定も精度判定も成立しないため、GPS 系ガードの先頭に置く（#3541）
        { guard: 'isGpsUnavailableForStamp', target: 'gpsUnavailableError' },
        // isInRange は粗い座標で算出されている可能性があるため、精度不足の判定を先に行う
        { guard: 'isGpsAccuracyInsufficient', target: 'gpsAccuracyError' },
        { guard: 'outOfRange', target: 'gpsRangeError' },
        { guard: 'hasPopup', target: 'popupShown' },
        { target: 'qrCheck' },
      ],
    },
    popupShown: {
      on: { POPUP_CLOSE: 'qrCheck' },
    },
    quizCheck: {
      always: [
        { guard: 'isQuizDailyLimitExhausted', target: 'quizDailyLimitError' },
        { guard: 'isDescriptiveQuiz', target: 'descriptiveQuizInput' },
        { guard: 'hasQuiz', target: 'quizChoice' },
        { target: 'acquiring' },
      ],
    },
    descriptiveQuizInput: {
      on: {
        TEXT_ANSWER_SUBMIT: {
          target: 'quizAnswering',
          actions: assign({
            pendingTextAnswer: ({ event }) => event.answer,
            pendingChoiceId: () => null,
          }),
        },
        QUIZ_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    quizChoice: {
      on: {
        ANSWER_SUBMIT: {
          target: 'quizAnswering',
          actions: assign({
            pendingChoiceId: ({ event }) => event.choiceId,
            pendingTextAnswer: () => null,
          }),
        },
        QUIZ_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    quizAnswering: {
      invoke: {
        src: 'answerQuiz',
        input: ({ context }): QuizAnswerInput => ({
          stampId: context.activeStampId as number,
          quizId: context.quizzes[0]?.id as number,
          choiceId: context.pendingChoiceId,
          textAnswer: context.pendingTextAnswer,
        }),
        onDone: [
          {
            guard: ({ event }) => event.output.quiz.isCorrect,
            target: 'quizCorrect',
            actions: assign({ quizAnswerResult: ({ event }) => event.output.quiz }),
          },
          {
            target: 'quizIncorrect',
            actions: assign({
              quizAnswerResult: ({ event }) => event.output.quiz,
              quizDailyLimitExhausted: ({ event }) => event.output.quiz.retry === false,
            }),
          },
        ],
        onError: [{ guard: 'isDescriptiveQuiz', target: 'descriptiveQuizInput' }, { target: 'quizChoice' }],
      },
    },
    quizCorrect: {
      on: {
        STAMP_GET: 'acquiring',
        QUIZ_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    quizIncorrect: {
      on: {
        QUIZ_RETRY: {
          guard: 'canRetryQuiz',
          target: 'quizCheck',
        },
        QUIZ_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    quizDailyLimitError: {
      on: {
        QUIZ_DAILY_LIMIT_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    qrCheck: {
      always: [
        { guard: 'isQuizDailyLimitExhausted', target: 'quizDailyLimitError' },
        { guard: 'isQrType', target: 'qrScan' },
        { guard: 'isBarcodeType', target: 'barcodeScan' },
        { guard: 'isOcrType', target: 'ocrWarningCheck' },
        { target: 'quizCheck' },
      ],
    },
    qrScan: {
      on: {
        QR_READ: [
          // 別スポットの QR はクイズを出さずにエラーにする（#3725）
          { guard: 'isQrSpotMismatch', target: 'qrWrongFormat' },
          {
            target: 'quizCheck',
            actions: assign({
              qrCode: ({ event }) => event.code,
            }),
          },
        ],
        QR_CLOSE: {
          target: 'idle',
          actions: assign({ qrCode: () => null }),
        },
        QR_WRONG_FORMAT: {
          target: 'qrWrongFormat',
        },
      },
    },
    barcodeScan: {
      on: {
        BARCODE_READ: {
          target: 'quizCheck',
          actions: assign({
            qrCode: ({ event }) => event.code,
          }),
        },
        BARCODE_CLOSE: {
          target: 'idle',
          actions: assign({ qrCode: () => null }),
        },
      },
    },
    // ---------------------------------------------------------------
    // OCR フロー
    // ---------------------------------------------------------------
    ocrWarningCheck: {
      // 「次回から表示しない」保存済みなら注意喚起を出さずにカメラへ（v1 と同じ挙動）
      always: [
        { guard: 'isOcrWarningSkipped', target: 'ocrCapture' },
        { guard: 'hasOcrWarning', target: 'ocrWarning' },
        { target: 'ocrCapture' },
      ],
    },
    ocrWarning: {
      on: {
        OCR_WARNING_CONFIRM: [
          { guard: 'isOcrWarningSkipNextChecked', target: 'ocrCapture', actions: 'saveOcrWarningSkip' },
          { target: 'ocrCapture' },
        ],
        OCR_WARNING_CLOSE: 'idle',
      },
    },
    ocrCapture: {
      on: {
        OCR_CAPTURED: {
          target: 'ocrPreview',
          actions: assign({
            ocrCapturedImage: ({ event }) => event.image,
          }),
        },
        OCR_CAPTURE_CLOSE: 'idle',
      },
    },
    // 撮影画像のプレビュー: ユーザーがピンぼけ・見切れを確認し、送信 / 再撮影 を選択する
    ocrPreview: {
      on: {
        // 送信: 既存の取得フローへ。quizCheck を経由して acquiring(POST) へ進む（画像は保持したまま）
        OCR_PREVIEW_SUBMIT: 'quizCheck',
        // 撮り直し: カメラへ戻し、保持していた撮影画像をクリア
        OCR_PREVIEW_RETAKE: {
          target: 'ocrCapture',
          actions: assign({ ocrCapturedImage: () => null }),
        },
        // 中断: スポット詳細へ戻し、撮影画像をクリア
        OCR_PREVIEW_CLOSE: {
          target: 'idle',
          actions: assign({ ocrCapturedImage: () => null }),
        },
      },
    },
    acquiring: {
      invoke: {
        src: 'acquireStamp',
        input: ({ context }): AcquireInput => ({
          stampId: context.activeStampId as number,
          lat: context.gps.lat,
          lon: context.gps.lon,
          gpsAvailable: context.gps.available,
          spotId: context.spotId,
          qrCode: context.qrCode,
          ocrImage: context.ocrCapturedImage,
          stampType: context.activeStampType ?? undefined,
        }),
        onDone: {
          target: 'stampShowing',
          actions: [
            assign({
              acquiredStamps: ({ event }) => event.output.stamps,
              acquiredRewards: ({ event }) => event.output.acquiredRewards,
              shareSns: ({ event }) => event.output.shareSns ?? null,
              currentStampIndex: 0,
              // GPS スタンプはバックエンドが同スポットの他スタンプも secondary で自動付与するため、
              // レスポンスの stampId をマージして獲得済み集合を差分更新する。
              acquiredStampIds: ({ context, event }) => {
                const merged = new Set(context.acquiredStampIds)
                for (const s of event.output.stamps) {
                  if (s.stampId != null) merged.add(s.stampId)
                }
                // stampId は nullable（UserStampAcquireData）。全件欠落しても獲得直後に
                // 「未獲得」へ戻さない（オーバーレイ・ARフォト解放・再表示導線の巻き戻り防止）ため、
                // 押した対象スタンプを獲得済みとして扱う。
                const hasResolvedStampId = event.output.stamps.some((s) => s.stampId != null)
                if (event.output.stamps.length > 0 && !hasResolvedStampId && context.activeStampId != null) {
                  merged.add(context.activeStampId)
                }
                return [...merged]
              },
              qrCode: () => null,
              ocrCapturedImage: () => null,
            }),
            // 上で更新した acquiredStampIds を読むため assign を分ける
            // （XState v5 の assign は同一オブジェクト内では更新前の context を参照するため）
            assign(({ context }) => {
              const stamps = context.spot?.stamps ?? []
              const acquiredIds = new Set(context.acquiredStampIds)
              const hasAllStamps = stamps.length > 0 && stamps.every((s) => acquiredIds.has(s.id))

              // 獲得直後はユーザースタンプを再フェッチせず、スタンプ単位の理由から再計算する。
              // 獲得できたスタンプは「獲得済み」に更新し、残りは初回フェッチ時の理由を維持する。
              // これにより「2件のうち1件を獲得したが、残り1件は期間外」のようなケースで
              // ボタンが活性のまま押せてエラーダイアログになるのを防ぐ。
              // リピートスタンプ/マイナビではサーバーの理由は ..._TODAY になり、リセット後は null に戻る。
              const unavailableReasonByStampId = { ...context.unavailableReasonByStampId }
              for (const id of acquiredIds) {
                unavailableReasonByStampId[id] = 'STAMP_ALREADY_ACQUIRED'
              }
              // 理由が未知（未認証・取得失敗）のスタンプは「獲得可能」に倒し、押させる側に寄せる。
              const entries = stamps.map((s) => {
                const reason = unavailableReasonByStampId[s.id] ?? null
                return { isAvailable: reason === null, unavailableReason: reason }
              })

              return {
                hasAnyStamp: acquiredIds.size > 0,
                hasAllStamps,
                unavailableReasonByStampId,
                stampUnavailableReason: resolveStampsUnavailableReason(entries),
              }
            }),
            // 残った未獲得スタンプを次の対象にする
            'resolveActiveStamp',
          ],
        },
        onError: [
          {
            guard: ({ event }) => {
              const err = event.error as { name?: string } | null
              return err?.name === 'OcrStampError'
            },
            target: 'ocrReadFailure',
            actions: assign({
              ocrFailureId: ({ event }) => {
                const err = event.error as { ocrId?: number } | null
                return err?.ocrId ?? null
              },
              ocrMaxRetry: ({ event }) => {
                const err = event.error as { maxRetry?: number | null } | null
                return err?.maxRetry ?? null
              },
            }),
          },
          {
            guard: ({ event }) => {
              const err = event.error as { code?: string } | null
              return err?.code === 'STAMP_CODE_MISMATCH'
            },
            target: 'qrWrongFormat',
          },
          {
            target: 'acquireError',
            actions: assign({
              acquireErrorCode: ({ event }) => {
                const err = event.error as { code?: string } | null
                return err?.code ?? 'error'
              },
              // 特典フロー（useRewardFlowStateMachine）と同じ扱い。
              // 公開不可・汎用文言は null が返るので、その場合は i18n の汎用文言にフォールバックする
              acquireErrorMessage: ({ event }) => extractApiErrorMessage(event.error),
            }),
          },
        ],
      },
    },
    // OCR読み取り失敗（リトライ優先ファネル）
    // リトライ残があれば再撮影を促し、使い切ったら GET で補填申請可否を確認して出し分ける
    ocrReadFailure: {
      initial: 'deciding',
      states: {
        deciding: {
          always: [
            { guard: 'canRetryOcr', target: 'retryable' }, // リトライ残あり → 再撮影
            { guard: 'hasOcrFailureId', target: 'checkingApplication' }, // 残なし & ocrId あり → 補填申請可否を確認
            { target: 'notApplicable' }, // ocrId なし → 補填申請不可
          ],
        },
        // リトライ可能: 「再撮影する」＋「閉じる」
        retryable: {},
        // リトライ切れ直後: GET /scan/request/{ocrId} で applicable を確認
        checkingApplication: {
          invoke: {
            src: 'fetchOcrScanRequest',
            input: ({ context }) => ({ ocrId: context.ocrFailureId as number }),
            onDone: [
              { guard: ({ event }) => event.output.applicable === true, target: 'applicable' },
              { target: 'notApplicable' },
            ],
            onError: { target: 'notApplicable' }, // 404/403/通信失敗はすべて申請不可扱い
          },
        },
        // 補填申請可: 「補填申請をする」＋「閉じる」
        applicable: {},
        // 補填申請不可: 専用文言＋「閉じる」のみ
        notApplicable: {},
      },
      // 遷移イベントは親レベルに置く（target はルート直下の兄弟状態を指す）
      on: {
        OCR_FAILURE_RETRY: {
          guard: 'canRetryOcr',
          target: 'ocrCapture',
          actions: assign({
            ocrRetryCount: ({ context }) => context.ocrRetryCount + 1, // 送信後の再撮影を1回消費
            ocrFailureId: () => null,
            ocrCapturedImage: () => null,
          }),
        },
        // /ocrForm への遷移と ocrId 受け渡しは detail.tsx 側で行う（ocrFailureId は null にしない）
        OCR_FAILURE_APPLY: {
          target: 'idle',
          actions: assign({ ocrCapturedImage: () => null }),
        },
        OCR_FAILURE_CLOSE: {
          target: 'idle',
          actions: assign({ ocrFailureId: () => null, ocrCapturedImage: () => null }),
        },
      },
    },
    acquireError: {
      on: {
        ACQUIRE_ERROR_CLOSE: {
          target: 'idle',
          // 撮影画像（base64・数 MB）と QR コードを context に残さない。
          // OCR の重複・上限・画質などの 400 もここに流れるようになったため（#3694）
          actions: [assign({ acquireErrorCode: null, acquireErrorMessage: null }), 'clearScanContext'],
        },
      },
    },
    qrWrongFormat: {
      on: {
        QR_WRONG_FORMAT_CLOSE: {
          target: 'idle',
        },
      },
    },
    gpsRangeError: {
      on: {
        GPS_RANGE_ERROR_CLOSE: 'idle',
        GPS_RANGE_ERROR_RETRY: 'gpsRefreshing',
      },
    },
    /**
     * 測位精度が不足しており獲得 API を呼ばずに終了した状態。
     *
     * 精度不足の主因は屋内・ビル街など測位環境であり「スポットから遠い」こととは別物のため、
     * 場所を移しての再測位を促す（#3541。#3445 時点は「スポットに近づいてください」だった）。
     */
    gpsAccuracyError: {
      on: {
        GPS_ACCURACY_ERROR_CLOSE: 'idle',
        GPS_ACCURACY_ERROR_RETRY: 'gpsRefreshing',
      },
    },
    /**
     * 位置情報を取得できず獲得 API を呼ばずに終了した状態（#3541）。
     * 権限拒否は設定変更を促し、それ以外は測位からのやり直しを促す。
     */
    gpsUnavailableError: {
      on: {
        GPS_UNAVAILABLE_ERROR_CLOSE: 'idle',
        GPS_UNAVAILABLE_ERROR_RETRY: 'gpsRefreshing',
      },
    },
    notAuthenticatedError: {
      on: {
        NOT_AUTH_ERROR_CLOSE: 'idle',
      },
    },
    stampShowing: {
      on: {
        STAMP_NEXT: [
          {
            guard: 'hasMoreStamps',
            actions: assign({
              currentStampIndex: ({ context }) => context.currentStampIndex + 1,
            }),
          },
          {
            target: 'rewardCheck',
            actions: assign({
              showArPhotoUnlocked: ({ context }) => context.photoFrameInitiallyStampRequired,
            }),
          },
        ],
      },
    },
    rewardCheck: {
      always: [{ guard: 'hasRewards', target: 'rewardShowing' }, { target: 'photoCheck' }],
    },
    rewardShowing: {
      on: {
        REWARD_CLOSE: 'photoCheck',
      },
    },
    photoCheck: {
      always: [{ guard: 'hasPhotoUpload', target: 'photoPost' }, { target: 'idle' }],
    },
    photoPost: {
      on: {
        PHOTO_CLOSE: 'idle',
      },
    },
    // ---------------------------------------------------------------
    // 再表示フロー
    // ---------------------------------------------------------------
    redisplayPopupCheck: {
      always: [{ guard: 'hasPopup', target: 'redisplayPopupShown' }, { target: 'redisplayStampShowing' }],
    },
    redisplayPopupShown: {
      on: { STAMP_REDISPLAY_POPUP_CLOSE: 'redisplayStampShowing' },
    },
    redisplayStampShowing: {
      on: {
        STAMP_REDISPLAY_STAMP_NEXT: [
          {
            guard: 'hasMoreRedisplayStamps',
            actions: assign({
              currentStampIndex: ({ context }) => context.currentStampIndex + 1,
            }),
          },
          { target: 'idle' },
        ],
      },
    },
    // ---------------------------------------------------------------
    // エラー
    // ---------------------------------------------------------------
    error: {
      on: { RESET: 'idle' },
    },

    // ---------------------------------------------------------------
    // 401 エラー時のトークンリフレッシュ待機
    // ---------------------------------------------------------------
    /**
     * 401 エラー発生時のトークンリフレッシュ待機状態。
     * AppMachineContext の tokenRefresh が waiting になったら REFRESH_COMPLETE を受信し spotFetching へ遷移する。
     * 30 秒以内に REFRESH_COMPLETE が届かない場合は error ステートへタイムアウトする。
     */
    waitingForRefresh: {
      after: { 10000: '#stampFlow.error' },
      on: { REFRESH_COMPLETE: '#stampFlow.initialLoading.spotFetching' },
    },
  },
})

// ---------------------------------------------------------------------------
// 公開フック
// ---------------------------------------------------------------------------

type StampFlowMachineOptions = {
  isBarCodeWithGPS: boolean
  isQRCodeWithGPS: boolean
  isKeywordWithGPS: boolean
  /** キャンペーン情報を取得済みか。未取得のうちは GPS 併用フラグを信用しない（isGpsRequired 参照） */
  isCampaignLoaded: boolean
}

export const useStampFlowStateMachine = (spotId: number, options: StampFlowMachineOptions) => {
  const { isBarCodeWithGPS, isQRCodeWithGPS, isKeywordWithGPS, isCampaignLoaded } = options
  const queryClient = useQueryClient()
  const spotFetch = useTanstackSpotFetchFn(spotId)
  const quizFetchFactory = useStampQuizFetchFnFactory()
  const spotWithLatLonFetchFactory = useTanstackSpotWithLatLonFetchFnFactory()
  const apiRef = useApiRef()
  const { LOCAL_STORAGE_OCR_CONFIRM_KEY } = useCampaignSlug()

  // Refs to capture latest values and prevent stale closures in actors
  const queryClientRef = useRef(queryClient)
  const spotFetchRef = useRef(spotFetch)
  const quizFetchFactoryRef = useRef(quizFetchFactory)
  const spotWithLatLonFetchFactoryRef = useRef(spotWithLatLonFetchFactory)
  queryClientRef.current = queryClient
  spotFetchRef.current = spotFetch
  quizFetchFactoryRef.current = quizFetchFactory
  spotWithLatLonFetchFactoryRef.current = spotWithLatLonFetchFactory

  const isQRCodeWithGPSRef = useRef(isQRCodeWithGPS)
  const isBarCodeWithGPSRef = useRef(isBarCodeWithGPS)
  const isKeywordWithGPSRef = useRef(isKeywordWithGPS)
  const isCampaignLoadedRef = useRef(isCampaignLoaded)
  isQRCodeWithGPSRef.current = isQRCodeWithGPS
  isBarCodeWithGPSRef.current = isBarCodeWithGPS
  isKeywordWithGPSRef.current = isKeywordWithGPS
  isCampaignLoadedRef.current = isCampaignLoaded
  /**
   * 評価時点のキャンペーン GPS 設定を読む。
   * 測位要否（fetchGPS）・押下時の測位要否（isGpsRequiredForActiveStamp）・獲得リクエストの座標送信（acquireStamp）で同じ値を使い、
   * フラグ追加時に片方だけ更新して判定が食い違うのを防ぐ。
   * ref のみを読むため、useMemo 内のアクター・ガードから初回レンダーの関数を呼んでも最新値になる。
   */
  const readGpsOptions = (): GpsRequirementOptions => ({
    isQRCodeWithGPS: isQRCodeWithGPSRef.current,
    isBarCodeWithGPS: isBarCodeWithGPSRef.current,
    isKeywordWithGPS: isKeywordWithGPSRef.current,
    isCampaignLoaded: isCampaignLoadedRef.current,
  })
  // hydration 前はキーにプレースホルダーが入り得るため、評価時点の最新値を ref 経由で読む
  const ocrConfirmKeyRef = useRef(LOCAL_STORAGE_OCR_CONFIRM_KEY)
  ocrConfirmKeyRef.current = LOCAL_STORAGE_OCR_CONFIRM_KEY

  // biome-ignore lint/correctness/useExhaustiveDependencies: actors use refs to access latest values; machine is recreated on spotId change via key={id} in pages/[campaign]/spots/detail.tsx, not by useMemo alone (XState v5 useIdleActorRef compares logic.config by reference, and provide() does not change config reference)
  const machine = useMemo(
    () =>
      stampFlowMachineBase.provide({
        actors: {
          fetchSpot: fromPromise(async () => queryClientRef.current.fetchQuery(spotFetchRef.current)),

          // GPS を使うスタンプがあるスポットでのみ測位する。
          // 不要な画面で精度が出るまで待たされないよう、v1 と同じ条件に揃えている。
          // AR フォト・音声ガイドの座標は押下時に測位する（detail.tsx / #3727）ため、ここでは理由にしない。
          fetchGPS: fromPromise<FetchGpsResult, FetchGpsInput>(async ({ input }) => {
            const options = readGpsOptions()
            const result = isGpsRequired(input, options) ? await getGPS(input.gateTimeoutMs) : gpsUnavailable()
            // 精度不足で獲得を止めてよいのは、対象スタンプ自身が位置情報を要する場合のみ。
            // キャンペーン未ロード時や別スタンプのために測位しただけのスポットでは止めない。
            //
            // isCampaignLoaded ではガードしない。GPS 種別はキャンペーン設定に依存せず常に座標を送るため、
            // 未ロードを理由に false へ倒すと粗い座標のまま獲得 API を呼んでしまう。
            // 併用種別（QR/バーコード/キーワード）は未ロード時にフラグが false となり座標自体を送らないため、
            // ここで止められなくても粗い座標が送信されることはない。
            return {
              ...result,
              requiredForStamp: isGpsRequiredForStamp(input.activeStampType, options),
            }
          }),

          // スタンプ件数分を並列取得して stampId → クイズ の map にする。
          // 失敗したスタンプはエントリを持たない（＝クイズなし扱い。従来の単件取得失敗時と同じ挙動）。
          // 1件の失敗で全スタンプのクイズを取りこぼさないよう allSettled で部分成功を残す。
          fetchQuizzes: fromPromise(async ({ input }: { input: QuizzesInput }) => {
            const results = await Promise.allSettled(
              input.stampIds.map(async (stampId) => {
                const res = await queryClientRef.current.fetchQuery(quizFetchFactoryRef.current(stampId))
                return [stampId, (res?.quizzes ?? []) as StampQuizDetail[]] as const
              }),
            )
            return Object.fromEntries(results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : [])))
          }),

          fetchSpotWithGps: fromPromise(async ({ input }: { input: SpotWithGpsInput }) =>
            queryClientRef.current.fetchQuery(
              spotWithLatLonFetchFactoryRef.current(input.spotId, input.lat, input.lon),
            ),
          ),

          fetchUserStamps: fromPromise(
            async ({ input }: { input: { stampIds: number[] } }): Promise<UserStampCheckResult> => {
              // 1件の失敗で獲得済み判定を丸ごと失う（＝獲得済みなのにオーバーレイが未獲得になる）のを
              // 避けるため allSettled で部分成功を残す。失敗分は下で「理由不明」として扱う。
              const results = await Promise.allSettled(
                input.stampIds.map((id) => apiRef.current.auth.fetchUserStamp(id)),
              )
              const stamps = results.flatMap((r) =>
                r.status === 'fulfilled' && r.value?.stamp != null ? [r.value.stamp] : [],
              )
              const acquiredStampIds = stamps.filter((s) => s.isAcquired === true).map((s) => s.id)

              // 獲得後にボタン活性を再計算するため、スタンプ単位の理由を保持する。
              // isAvailable=false なのに理由が null（未知コードの縮退）のときは
              // resolveStampsUnavailableReason と同じく汎用理由に倒す。
              const unavailableReasonByStampId: Record<number, StampUnavailableReason | null> = {}
              for (const s of stamps) {
                unavailableReasonByStampId[s.id] = s.isAvailable ? null : (s.unavailableReason ?? 'STAMP_NOT_AVAILABLE')
              }

              // 取得できなかったスタンプ（リクエスト失敗・{stamp: null}）は「獲得可能」に倒して押させる。
              // 獲得直後の再計算（acquireStamp の onDone）と同じ方針。
              const stampById = new Map(stamps.map((s) => [s.id, s]))
              const entries = input.stampIds.map(
                (id) => stampById.get(id) ?? { isAvailable: true, unavailableReason: null },
              )

              return {
                hasAnyStamp: acquiredStampIds.length > 0,
                // 分母は取得できたユーザースタンプ数ではなく、スポットに紐づく全スタンプ数。
                // 一部が {stamp: null} で返った場合に「全件獲得済み」と誤判定しないため。
                hasAllStamps: input.stampIds.length > 0 && acquiredStampIds.length === input.stampIds.length,
                acquiredStampIds,
                unavailableReasonByStampId,
                unavailableReason: resolveStampsUnavailableReason(entries),
              }
            },
          ),

          fetchPhotoFrames: fromPromise(async ({ input }: { input: FetchPhotoFramesInput }) => {
            if (input.isAuthenticated) {
              return await apiRef.current.auth.postSpotPhotoFrames(input.spotId, {})
            }
            return await apiRef.current.guest.postSpotPhotoFrames(input.spotId, {})
          }),

          answerQuiz: fromPromise(async ({ input }: { input: QuizAnswerInput }) => {
            const body: Record<string, unknown> = {}
            if (input.choiceId !== null) body.answerId = input.choiceId
            else if (input.textAnswer !== null) body.answer = input.textAnswer
            return await apiRef.current.auth.postStampQuizAnswer(input.stampId, input.quizId, body)
          }),

          acquireStamp: fromPromise(async ({ input }: { input: AcquireInput }) => {
            const body = buildAcquireStampBody(input, readGpsOptions())

            if (input.stampType === 'OCR') {
              return await apiRef.current.auth.postOcrStamp(input.stampId, body)
            }
            return await apiRef.current.auth.postStamp(input.stampId, body)
          }),

          // 補填申請可否の確認（リトライ切れ時）。fetchQuery 経由で /ocrForm と同一 queryKey のキャッシュを共有する
          fetchOcrScanRequest: fromPromise(async ({ input }: { input: { ocrId: number } }) =>
            queryClientRef.current.fetchQuery(ocrScanRequestQueryOptions(input.ocrId, apiRef.current)),
          ),
        },
        guards: {
          isGpsRequiredForActiveStamp: ({ context }) =>
            shouldRefreshGpsOnStart(context.activeStampType, readGpsOptions()),
          // localStorage が例外を投げる環境（Safari プライベートモード等）では注意喚起を出す側に倒す
          isOcrWarningSkipped: () => {
            try {
              return localStorage.getItem(ocrConfirmKeyRef.current) === '1'
            } catch {
              return false
            }
          },
        },
        actions: {
          saveOcrWarningSkip: () => {
            try {
              localStorage.setItem(ocrConfirmKeyRef.current, '1')
            } catch {
              // 保存できなくても撮影フローは止めない
            }
          },
        },
      }),
    [spotId],
  )

  const [state, send] = useMachine(machine, {
    input: (() => {
      // Zustand の同期 getState() でマシン生成時点の認証状態を取得。
      // loading が false なら waitingForAuth を即スキップして spotFetching へ進む。
      // まだ loading 中なら waitingForAuth で AUTH_READY イベントを待つ。
      const tokenState = useTokenStore.getState()
      return {
        spotId,
        isAuthenticated: !tokenState.loading && tokenState.idToken !== null,
        isAuthInitialized: !tokenState.loading,
      }
    })(),
  })

  /** 対象スタンプがキャンペーンの GPS 設定上、位置情報を要するか（キャンペーンのロード状態は見ない） */
  const activeStampRequiresGps = isGpsRequiredForStamp(state.context.activeStampType, {
    isQRCodeWithGPS,
    isBarCodeWithGPS,
    isKeywordWithGPS,
  })

  return {
    state,
    send,
    isLoading: state.matches('initialLoading') || state.matches('waitingForRefresh'),
    isError: state.matches('error'),
    isIdle: state.matches('idle'),
    isGpsRefreshing: state.matches('gpsRefreshing'),
    /** 測位後に spot.isInRange を取り直している状態（#3541）。UI 上は測位中と同じ扱いにする */
    isSpotRangeRefreshing: state.matches('spotRangeRefreshing'),
    isPopupShown: state.matches('popupShown'),
    isDescriptiveQuizInput: state.matches('descriptiveQuizInput'),
    isQuizChoice: state.matches('quizChoice'),
    isQuizAnswering: state.matches('quizAnswering'),
    isQuizCorrect: state.matches('quizCorrect'),
    isQuizIncorrect: state.matches('quizIncorrect'),
    isQuizDailyLimitError: state.matches('quizDailyLimitError'),
    isQrScan: state.matches('qrScan'),
    isBarcodeScan: state.matches('barcodeScan'),
    isOcrWarning: state.matches('ocrWarning'),
    isOcrCapture: state.matches('ocrCapture'),
    isOcrPreview: state.matches('ocrPreview'),
    isOcrReadFailure: state.matches('ocrReadFailure'),
    isOcrRetryable: state.matches({ ocrReadFailure: 'retryable' }),
    isOcrCheckingApplication: state.matches({ ocrReadFailure: 'checkingApplication' }),
    isOcrApplicable: state.matches({ ocrReadFailure: 'applicable' }),
    isOcrNotApplicable: state.matches({ ocrReadFailure: 'notApplicable' }),
    isAcquiring: state.matches('acquiring'),
    isAcquireError: state.matches('acquireError'),
    isQrWrongFormat: state.matches('qrWrongFormat'),
    isGpsRangeError: state.matches('gpsRangeError'),
    isGpsAccuracyError: state.matches('gpsAccuracyError'),
    isGpsUnavailableError: state.matches('gpsUnavailableError'),
    /** 位置情報を取得できなかった理由。ダイアログの文言出し分けに使う（#3541） */
    gpsFailureReason: state.context.gps.failureReason,
    isNotAuthenticatedError: state.matches('notAuthenticatedError'),
    isStampShowing: state.matches('stampShowing'),
    isRewardShowing: state.matches('rewardShowing'),
    isPhotoPost: state.matches('photoPost'),
    isArPhotoUnlockedShowing: state.context.showArPhotoUnlocked,
    isRedisplayStampShowing: state.matches('redisplayStampShowing'),
    isRedisplayPopupShown: state.matches('redisplayPopupShown'),
    context: state.context,
    gpsInvalid: state.context.gps.loaded && state.context.gps.accuracy > GPS_ACCURACY_THRESHOLD,
    /**
     * 対象スタンプの獲得に位置情報が必要か。
     * 位置情報を取得できていないときにボタンを止める判定に使う。
     * キャンペーン情報が未取得のうちは false（誤って「許可してください」を出さないため。
     * 測位するかどうかの判定〈isGpsRequired〉とは逆方向に倒している）。
     */
    stampRequiresGps: isCampaignLoaded && activeStampRequiresGps,
    /**
     * 押下時の測位中（gpsRefreshing / spotRangeRefreshing）に「位置情報を取得中...」を出してよいか（#3725）。
     *
     * stampRequiresGps と違いキャンペーンのロード状態では止めない。GPS 種別はキャンペーン設定に依存せず
     * 位置情報を要するため、未ロードを理由に false へ倒すと実際に測位しているのに「処理中」になる。
     * GPS 併用種別（QR 等）は未ロード時にフラグが false のため false になり、「処理中」を出す。
     */
    isAcquiringLocationForStamp: activeStampRequiresGps,
    quizAnswerResult: state.context.quizAnswerResult,
    canRetryQuiz: state.context.quizAnswerResult?.retry !== false,
    ocrFailureId: state.context.ocrFailureId,
    ocrCapturedImage: state.context.ocrCapturedImage,
  }
}
